import React from "react";
import Select from "react-select";
import { onlyNumericInput, formatDate } from "@/utils";
import { driverTypeOptions, localCodeOptions } from "@/constants/selectOptions";
import { useVerifyDriverLicenseMutation } from "@/redux/driverLicenseApi";

export const AdditionalDriverInfo = ({ formData, handleChange, setFormData, getFileName, handleCommonFileChange, contractIndex }) => {
    const additionalDriver =
        formData.contracts[contractIndex]?.contractDrivers?.find((driver) => driver.contractDriverType === "ADDITIONAL") || {};
    const [verifyDriverLicense] = useVerifyDriverLicenseMutation();

    const updateDriverField = (value, driverType, fieldName) => {
        setFormData((prev) => {
            const updatedContracts = [...prev.contracts];
            const contract = updatedContracts[contractIndex];

            const updatedDrivers = contract.contractDrivers.map((driver) => {
                if (driver.contractDriverType === driverType) {
                    return { ...driver, [fieldName]: value };
                }
                return driver;
            });

            updatedContracts[contractIndex] = {
                ...contract,
                contractDrivers: updatedDrivers,
            };

            return { ...prev, contracts: updatedContracts };
        });
    };

    const handleLicenseVerification = async () => {
        if (!additionalDriver.name?.trim()) {
            setFormData((prev) => ({ ...prev, toastMsg: "이름을 입력해주세요." }));
            return;
        }

        if (!additionalDriver.driverLicenseType) {
            setFormData((prev) => ({ ...prev, toastMsg: "면허구분을 선택해주세요." }));
            return;
        }

        if (!additionalDriver.localCode) {
            setFormData((prev) => ({ ...prev, toastMsg: "지역을 선택해주세요." }));
            return;
        }
        if (!additionalDriver.driverLicenseNumber || additionalDriver.driverLicenseNumber.length !== 10) {
            setFormData((prev) => ({ ...prev, toastMsg: "지역선택 시 면허번호 숫자 10자리를 입력해주세요." }));
            return;
        }
        const licenseNumber = `${additionalDriver.localCode}${additionalDriver.driverLicenseNumber}`;

        try {
            const verificationResponse = await verifyDriverLicense({
                name: additionalDriver.name,
                license_number: licenseNumber,
                license_type: additionalDriver.driverLicenseType,
                contract_driver_id: additionalDriver.id || null,
            }).unwrap();

            setFormData((prev) => {
                const updatedContracts = [...prev.contracts];
                const contract = updatedContracts[contractIndex];

                const updatedDrivers = contract.contractDrivers.map((driver) => {
                    if (driver.contractDriverType === "ADDITIONAL") {
                        return { ...driver, licenseVerifyYn: "Y" };
                    }
                    return driver;
                });

                updatedContracts[contractIndex] = {
                    ...contract,
                    contractDrivers: updatedDrivers,
                };

                return {
                    ...prev,
                    contracts: updatedContracts,
                    toastMsg: "면허 검증 성공",
                };
            });
        } catch (error) {
            setFormData((prev) => ({ ...prev, toastMsg: error?.message || "면허 검증에 실패했습니다." }));
        }
    };

    // 초기화 버튼 핸들러
    const handleResetVerification = () => {
        updateDriverField("N", "ADDITIONAL", "licenseVerifyYn");
    };

    // 운전면허증 파일 업로드 핸들러
    const handleDriverLicenseUpload = async (e) => {
        if (handleCommonFileChange) {
            handleCommonFileChange(e, "DRIVER_LICENSE_ADDITIONAL");
        }
    };

    // 주소 검색 팝업 핸들러
    const handleAddressSearch = () => {
        if (setFormData) {
            setFormData((prevState) => ({
                ...prevState,
                addressPopupStatus: true,
                addressPopupTarget: `ADDITIONAL_${contractIndex}`,
            }));
        }
    };

    return (
        <>
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>
                        고객이름
                    </span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            name="name"
                            value={additionalDriver?.name || ""}
                            placeholder="고객이름"
                            onChange={(e) => updateDriverField(e.target.value, "ADDITIONAL", e.target.name)}
                            disabled={additionalDriver.licenseVerifyYn === "Y"}
                        />
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>
                        주민번호
                    </span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            name="idNumberF"
                            value={additionalDriver?.idNumberF || ""}
                            placeholder=""
                            onChange={(e) => updateDriverField(e.target.value, "ADDITIONAL", e.target.name)}
                            onInput={onlyNumericInput}
                            maxLength={6}
                            disabled={additionalDriver.licenseVerifyYn === "Y"}
                        />
                    </div>
                    <span className="text-esset">-</span>
                    <div className="form-element">
                        <input
                            type="text"
                            name="idNumberB"
                            value={additionalDriver?.idNumberB || ""}
                            placeholder=""
                            onChange={(e) => updateDriverField(e.target.value, "ADDITIONAL", e.target.name)}
                            onInput={onlyNumericInput}
                            maxLength={7}
                            disabled={additionalDriver.licenseVerifyYn === "Y"}
                        />
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>
                        연락처 <em>*</em>
                    </span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            name="phone"
                            value={additionalDriver?.phone || ""}
                            placeholder=""
                            onChange={(e) => updateDriverField(e.target.value, "ADDITIONAL", e.target.name)}
                            onInput={onlyNumericInput}
                            maxLength={11}
                        />
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>면허구분</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            options={driverTypeOptions}
                            className="custom-select"
                            classNamePrefix="custom-select"
                            isSearchable={false}
                            placeholder="면허구분"
                            value={driverTypeOptions.find((opt) => opt.value === additionalDriver?.driverLicenseType)}
                            onChange={(option) => updateDriverField(option.value, "ADDITIONAL", "driverLicenseType")}
                            isDisabled={additionalDriver.licenseVerifyYn === "Y"}
                        />
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>면허번호</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <div className="field-row">
                            <Select
                                options={localCodeOptions}
                                className="custom-select"
                                classNamePrefix="custom-select"
                                isSearchable={false}
                                placeholder="지역"
                                value={localCodeOptions.find((option) => option.value === additionalDriver.localCode) || null}
                                onChange={(option) => updateDriverField(option.value, "ADDITIONAL", "localCode")}
                                isDisabled={additionalDriver.licenseVerifyYn === "Y"}
                            />
                        </div>
                    </div>
                    <div className="form-element">
                        <input
                            type="text"
                            name="driverLicenseNumber"
                            value={additionalDriver?.driverLicenseNumber || ""}
                            placeholder="면허번호"
                            onChange={(e) => updateDriverField(e.target.value, "ADDITIONAL", e.target.name)}
                            onInput={onlyNumericInput}
                            maxLength={10}
                            disabled={additionalDriver.licenseVerifyYn === "Y"}
                        />
                    </div>
                    <div className="form-element_button">
                        {additionalDriver.licenseVerifyYn === "Y" ? (
                            <button type="button" className="btn-form_element btn-outline" onClick={handleResetVerification}>
                                초기화
                            </button>
                        ) : (
                            <button type="button" className="btn-form_element btn-outline" onClick={handleLicenseVerification}>
                                검증
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>면허 검증여부</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            name="licenseVerifyYn"
                            value={additionalDriver?.licenseVerifyYn === "Y" ? "검증 완료" : "검증 대기"}
                            placeholder=""
                            onChange={(e) => updateDriverField(e.target.value, "ADDITIONAL", e.target.name)}
                            disabled
                        />
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="1">
                <div className="row-head">
                    <span>
                        주소
                    </span>
                </div>
                <div className="row-content">
                    <div className="form-element" style={{ display: "none" }}>
                        <input
                            type="text"
                            placeholder=""
                            name="bCode"
                            value={additionalDriver?.bCode || ""}
                            onChange={(e) => updateDriverField(e.target.value, "ADDITIONAL", e.target.name)}
                            readOnly
                            onClick={handleAddressSearch}
                        />
                    </div>
                    <div className="form-element" style={{ width: "668px" }}>
                        <input
                            type="text"
                            placeholder=""
                            name="address"
                            value={additionalDriver?.address || ""}
                            onChange={(e) => updateDriverField(e.target.value, "ADDITIONAL", e.target.name)}
                            readOnly
                            onClick={handleAddressSearch}
                        />
                    </div>
                    <div className="form-element">
                        <input
                            type="text"
                            placeholder="상세 주소를 입력해주세요."
                            name="addressDetail"
                            value={additionalDriver?.addressDetail || ""}
                            onChange={(e) => updateDriverField(e.target.value, "ADDITIONAL", e.target.name)}
                        />
                    </div>
                    <div className="form-element_button">
                        <button type="button" className="btn-form_element btn-outline" onClick={handleAddressSearch}>
                            주소검색
                        </button>
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="1">
                <div className="row-head">
                    <span>운전면허증</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            readOnly
                            value={getFileName(`DRIVER_LICENSE_ADDITIONAL_${contractIndex}`)}
                            placeholder="이미지 파일을 첨부해주세요."
                            onClick={() => {
                                const uploadItem = formData.contractUploads?.find(
                                    (item) => item.uploadType === `DRIVER_LICENSE_ADDITIONAL_${contractIndex}`
                                );
                                if (uploadItem && uploadItem.upload && uploadItem.upload.url) {
                                    window.open(uploadItem.upload.url, "_blank");
                                } else {
                                    document.getElementById(`driverLicenseInput_ADDITIONAL_${contractIndex}`).click();
                                }
                            }}
                        />
                    </div>
                    <div className="form-element_button">
                        <button
                            type="button"
                            className="btn-form_element btn-outline"
                            onClick={() => document.getElementById(`driverLicenseInput_ADDITIONAL_${contractIndex}`).click()}
                        >
                            파일첨부
                        </button>
                        <input
                            type="file"
                            id={`driverLicenseInput_ADDITIONAL_${contractIndex}`}
                            style={{ display: "none" }}
                            accept=".pdf,.jpg,.png"
                            onChange={handleDriverLicenseUpload}
                        />
                    </div>
                </div>
            </div>
        </>
    );
};
